
// ============================================================
// 2. src/utils/logHelper.js (수정본)
// ============================================================

const MODE = process.env.NODE_ENV || 'development';
const IS_DEV = MODE === 'development';

// ✅ 로거 저장소
let _logger = null;

// ---- 민감정보 마스킹 -------------------------------------------
function sanitize(arg) {
  if (!arg) return arg;

  try {
    if (typeof arg === 'string') {
      return arg
        .replace(/(Bearer\s+)[\w.-]+/gi, '$1***')
        .replace(/([?&](token|apikey|password|secret)=)[^&]+/gi, '$1***')
        .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '***@***.***');
    }

    if (typeof arg === 'object') {
      const seen = new WeakSet();

      const clone = JSON.parse(JSON.stringify(arg, (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) return '[Circular]';
          seen.add(value);
        }
        return value;
      }));

      ['headers', 'config'].forEach(key => {
        const headers = clone[key]?.headers || clone[key];
        if (headers && typeof headers === 'object') {
          Object.keys(headers).forEach(h => {
            if (/authorization|cookie|token|apikey|password/i.test(h)) {
              headers[h] = '***';
            }
          });
        }
      });

      if (clone.url) {
        clone.url = String(clone.url)
          .replace(/([?&](token|apikey|password)=)[^&]+/gi, '$1***');
      }

      return clone;
    }

    return arg;
  } catch (err) {
    console.error('Sanitize failed:', err);
    return '[sanitize error]';
  }
}

// ---- 스택 트레이스 ---------------------------------------------
function buildStack(skip = 3) {
  if (!IS_DEV) return '';

  try {
    const stack = new Error().stack;
    if (!stack) return '';

    return stack
      .split('\n')
      .slice(skip)
      .map(line => line
        .replace(/webpack:\/\/\/\.\//g, '')
        .replace(/\?[a-f0-9]+/g, '')
        .trim()
      )
      .filter(line => !line.includes('node_modules'))
      .slice(0, 5)
      .join('\n');
  } catch {
    return '';
  }
}

// ---- Logger 접근 -----------------------------------------------
function getLogger() {
  if (_logger) return _logger;

  return {
    error: console.error.bind(console),
    warn: console.warn.bind(console),
    info: console.info.bind(console),
    debug: console.debug.bind(console),
    log: console.log.bind(console)
  };
}

// ✅ 로거 주입 (logger.js에서 호출)
export function setLogger(logger) {
  if (_logger) {
    if (IS_DEV) console.warn('Logger already initialized');
    return;
  }
  
  _logger = logger;
  
  if (IS_DEV) {
    console.info('✅ Logger initialized');
  }
}

// ---- 공통 출력 ---------------------------------------------------
function out(kind, ...args) {
  const safeArgs = args.map(sanitize);
  const logger = getLogger();

  if (IS_DEV) {
    const stack = buildStack(4);
    logger[kind](...safeArgs, stack ? `\n📍 ${stack}` : '');
  } else {
    logger[kind](...safeArgs);
  }
}

// ---- 운영 리포팅 -------------------------------------------------
export function report(error, context = {}) {
  if (IS_DEV) return;

  try {
    const Sentry = window?.Sentry;
    if (!Sentry) return;

    if (error instanceof Error) {
      Sentry.captureException(error, {
        level: 'error',
        tags: { source: 'report' },
        extra: context
      });
    } else {
      Sentry.captureMessage(String(error), {
        level: 'error',
        extra: { originalError: error, ...context }
      });
    }
  } catch (err) {
    console.error('Report failed:', err);
  }
}

// ---- 퍼블릭 API ------------------------------------------------
export const logError = (...args) => out('error', ...args);
export const logWarn = (...args) => out('warn', ...args);
export const logInfo = IS_DEV ? (...args) => out('info', ...args) : () => {};
export const logDebug = IS_DEV ? (...args) => out('debug', ...args) : () => {};

// ✅ 성능 측정 유틸
export function logPerf(label) {
  if (!IS_DEV) return { end: () => {} };

  const start = performance.now();
  const logger = getLogger();
  logger.debug(`⏱️ [Start] ${label}`);

  return {
    end: () => {
      const duration = (performance.now() - start).toFixed(2);
      logger.debug(`⏱️ [End] ${label} - ${duration}ms`);
    }
  };
}